import React, { useEffect, useState, useCallback } from "react";
import { Cookie, ShieldCheck, BarChart3, Megaphone, X } from "lucide-react";
import { saveConsent, getConsent, OPEN_SETTINGS_EVENT, DEFAULT_DENIED } from "../lib/consent";
import { applyConsent } from "../lib/tracking";
import { Link } from "react-router-dom";

const CATEGORIES = [
  {
    key: "necessary",
    icon: ShieldCheck,
    title: "Cookies necesare",
    desc: "Esențiale pentru funcționarea site-ului (sesiune, preferință consimțământ, securitate). Nu pot fi dezactivate.",
    locked: true,
  },
  {
    key: "analytics",
    icon: BarChart3,
    title: "Cookies de analiză",
    desc: "Ne ajută să înțelegem cum este folosit site-ul (Google Analytics 4 și Microsoft Clarity — heatmaps și înregistrări de sesiune cu mascarea datelor sensibile). Datele sunt agregate și anonimizate.",
  },
  {
    key: "marketing",
    icon: Megaphone,
    title: "Cookies de marketing",
    desc: "Folosite pentru a-ți afișa anunțuri relevante și a măsura performanța campaniilor (Meta Pixel, Google Ads).",
  },
];

export default function CookieConsent() {
  const [open, setOpen] = useState(false);
  const [showCustom, setShowCustom] = useState(false);
  const [prefs, setPrefs] = useState(DEFAULT_DENIED);

  const showBanner = useCallback(() => {
    const stored = getConsent();
    setPrefs(stored || DEFAULT_DENIED);
    setOpen(true);
    setShowCustom(false);
  }, []);

  useEffect(() => {
    const stored = getConsent();
    if (!stored) {
      setOpen(true);
    }
    const handler = () => showBanner();
    window.addEventListener(OPEN_SETTINGS_EVENT, handler);
    return () => window.removeEventListener(OPEN_SETTINGS_EVENT, handler);
  }, [showBanner]);

  const apply = (consent) => {
    const saved = saveConsent(consent);
    applyConsent(saved);
    setOpen(false);
    setShowCustom(false);
  };

  const acceptAll = () => apply({ analytics: true, marketing: true });
  const rejectAll = () => apply({ analytics: false, marketing: false });
  const saveCustom = () => apply({ analytics: prefs.analytics, marketing: prefs.marketing });

  if (!open) return null;

  return (
    <>
      {/* Backdrop only when customizing — softer when in banner mode */}
      {showCustom && (
        <div className="fixed inset-0 bg-[#373A45]/40 backdrop-blur-sm z-40" />
      )}

      <div
        className="fixed bottom-0 inset-x-0 z-50 p-3 md:p-4"
        role="dialog"
        aria-labelledby="cookie-consent-title"
        aria-describedby="cookie-consent-desc"
        data-testid="cookie-consent-banner"
      >
        <div className="max-w-5xl mx-auto bg-white rounded-2xl border border-[#E5E7EB] shadow-2xl overflow-hidden">
          {/* Compact banner */}
          {!showCustom && (
            <div className="p-5 md:p-6">
              <div className="flex flex-col md:flex-row md:items-start md:gap-6">
                <div className="flex items-start gap-3 flex-1">
                  <div className="hidden sm:grid h-10 w-10 rounded-xl bg-[#F0E7F4] place-items-center text-[#965EA7] shrink-0">
                    <Cookie size={18} />
                  </div>
                  <div className="text-sm text-[#373A45]">
                    <h2 id="cookie-consent-title" className="font-display text-base font-bold">
                      Confidențialitatea ta contează
                    </h2>
                    <p id="cookie-consent-desc" className="mt-1 leading-relaxed">
                      Folosim cookies necesare pentru funcționarea site-ului și, cu acordul tău,
                      cookies de analiză și marketing pentru a îmbunătăți experiența și relevanța
                      conținutului. Poți schimba opțiunile oricând.{" "}
                      <Link to="/politica-cookies" className="underline text-[#965EA7] hover:text-[#7A4986]">
                        Politica de cookies
                      </Link>{" "}·{" "}
                      <Link to="/politica-confidentialitate" className="underline text-[#965EA7] hover:text-[#7A4986]">
                        Politica de confidențialitate
                      </Link>
                    </p>
                  </div>
                </div>

                <div className="mt-5 md:mt-0 flex flex-col sm:flex-row gap-2 md:shrink-0">
                  <button
                    onClick={rejectAll}
                    className="rounded-full px-5 py-2.5 text-sm font-semibold bg-[#373A45] text-white hover:bg-[#1F2128] transition-colors"
                    data-testid="cookie-decline"
                  >
                    Refuz toate
                  </button>
                  <button
                    onClick={() => setShowCustom(true)}
                    className="rounded-full px-5 py-2.5 text-sm font-semibold border-2 border-[#373A45] text-[#373A45] hover:bg-[#F0E7F4] hover:border-[#965EA7] hover:text-[#965EA7] transition-colors"
                    data-testid="cookie-customize"
                  >
                    Personalizează
                  </button>
                  <button
                    onClick={acceptAll}
                    className="rounded-full px-5 py-2.5 text-sm font-semibold bg-[#965EA7] text-white hover:bg-[#7A4986] transition-colors"
                    data-testid="cookie-accept"
                  >
                    Accept toate
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Custom panel */}
          {showCustom && (
            <div className="p-5 md:p-7" data-testid="cookie-customize-panel">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="font-display text-lg md:text-xl font-bold text-[#373A45]">
                    Setări cookies
                  </h2>
                  <p className="text-sm text-[#6B7280] mt-1">
                    Activează doar categoriile cu care ești de acord. Poți modifica oricând din footer.
                  </p>
                </div>
                <button
                  onClick={() => setShowCustom(false)}
                  className="p-2 rounded-lg hover:bg-[#F5F6F6]"
                  aria-label="Închide"
                  data-testid="cookie-customize-close"
                >
                  <X size={18} />
                </button>
              </div>

              <ul className="mt-5 space-y-3">
                {CATEGORIES.map((cat) => {
                  const Icon = cat.icon;
                  const isOn = !!prefs[cat.key];
                  return (
                    <li key={cat.key} className="border border-[#E5E7EB] rounded-2xl p-4 flex items-start gap-4" data-testid={`cookie-cat-${cat.key}`}>
                      <div className="h-10 w-10 rounded-xl bg-[#F5F6F6] grid place-items-center text-[#373A45] shrink-0">
                        <Icon size={18} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between gap-3">
                          <div className="font-semibold text-[#373A45]">{cat.title}</div>
                          <Toggle
                            on={isOn}
                            disabled={cat.locked}
                            onChange={(v) => setPrefs((p) => ({ ...p, [cat.key]: v }))}
                            testid={`cookie-toggle-${cat.key}`}
                            label={cat.title}
                          />
                        </div>
                        <p className="text-sm text-[#6B7280] mt-1 leading-relaxed">{cat.desc}</p>
                      </div>
                    </li>
                  );
                })}
              </ul>

              <div className="mt-6 flex flex-col sm:flex-row gap-2 sm:justify-end">
                <button
                  onClick={rejectAll}
                  className="rounded-full px-5 py-2.5 text-sm font-semibold border-2 border-[#373A45] text-[#373A45] hover:bg-[#373A45] hover:text-white transition-colors"
                  data-testid="cookie-customize-reject"
                >
                  Refuz toate
                </button>
                <button
                  onClick={saveCustom}
                  className="rounded-full px-5 py-2.5 text-sm font-semibold bg-[#965EA7] text-white hover:bg-[#7A4986] transition-colors"
                  data-testid="cookie-customize-save"
                >
                  Salvează preferințele
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function Toggle({ on, disabled, onChange, testid, label }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      disabled={disabled}
      onClick={() => !disabled && onChange(!on)}
      data-testid={testid}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors shrink-0 ${
        disabled ? "bg-[#519872] opacity-70 cursor-not-allowed" : on ? "bg-[#965EA7]" : "bg-[#E5E7EB]"
      }`}
    >
      <span
        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-sm transition-transform ${
          on ? "translate-x-5" : "translate-x-0.5"
        }`}
      />
    </button>
  );
}
